import { useEffect, useId, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * 習熟度の行動記述 (behavior_description) を伴走表示する「?」アイコン (要件§3-4)。
 * ⚠️ [実装後レビューで是正、design-expert High]: `title`属性のみのツールチップはタッチ端末で
 * 開けない (hoverが無い)。InheritanceSourceBadge.tsx と同じ「クリックで開くポップオーバー」
 * パターンに統一する (`title`属性は使わない、ui-guidelines.md §2.4 — スクリーンリーダーが
 * 読み上げないため)。認定確定・人材像マトリクス・スキル登録の3箇所で共有する。
 */
export function BehaviorDescriptionHint({ text, size = 12 }: { text: string; size?: number }) {
  const [open, setOpen] = useState(false);
  const descId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-describedby={descId}
        aria-expanded={open}
        aria-label="判定基準(行動記述)を表示"
        className="inline-flex items-center justify-center w-[26px] h-[26px] -m-1.5 text-text-sub hover:text-text-main focus-ring rounded"
      >
        <HelpCircle size={size} aria-hidden />
      </button>
      <span id={descId} className="sr-only">{text}</span>
      {open && (
        <div
          role="tooltip"
          className="absolute z-50 left-0 top-full mt-1 w-max max-w-[260px] rounded border border-surface-border bg-white shadow-lg px-3 py-2 text-xs text-text-body break-words"
        >
          {text}
        </div>
      )}
    </div>
  );
}
